import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'oidc-client-ts';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import {
    Avatar,
    Collapse,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiAccountCircle,
    mdiAccountMultipleOutline,
    mdiChevronDown,
    mdiChevronUp,
    mdiLogoutVariant,
} from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import ApiService from '../../services/apiService';
import AuthService from '../../services/authService';
import { publicRoutes } from '../../routes/appRouteList';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import OverlaySpinner from '../Loaders/OverlaySpinner';

const classesPrefix = 'userMenu';

const classes = {
    menu: `${classesPrefix}-menu`,
    listItem: `${classesPrefix}-listItem`,
    listItemSelected: `${classesPrefix}-listItemSelected`,
    nested: `${classesPrefix}-nested`,
};

const StyledMenu = styled(Menu)(({ theme }) => {
    return {
        [`& .${classes.menu}`]: {
            minWidth: '300px',
        },
        [`& .${classes.listItem}`]: {
            color: theme.palette.text.primary,
            '& .MuiListItemIcon-root': {
                color: theme.palette.secondary.main,
            },
            '&:hover': {
                '& .MuiListItemIcon-root': {
                    color: theme.palette.secondary.main,
                },
            },
        },
        [`& .${classes.listItemSelected}`]: {
            color: theme.palette.primary.main,
            '&.Mui-selected': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.common.white,
                '& .MuiListItemText-root': {
                    color: theme.palette.common.white,
                },
                '&:hover': {
                    backgroundColor: theme.palette.secondary.light,
                },
                '& .MuiListItemIcon-root': {
                    color: 'inherit',
                },
            },
            '& .MuiListItemIcon-root': {
                color: 'inherit',
            },
        },
        [`& .${classes.nested}`]: {
            paddingLeft: theme.spacing(4),
        },
    };
});

const UserMenu = (): React.JSX.Element => {
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
    const [isAccountsOpen, setIsAccountsOpen] = useState(false);
    const [user, setUser] = useState<User | null>(null);

    const userLoginStatus = useAppSelector((state) => { return state.user.userLoginStatus; });
    const userDetails = useAppSelector((state) => { return state.user.userDetails; });

    useEffect((): void => {
        if (userLoginStatus === ApiStatus.Success) {
            (async (): Promise<void> => {
                try {
                    const authenticatedUser = await AuthService.getUser();
                    if (authenticatedUser !== null) {
                        setUser(authenticatedUser);
                    }
                } catch {
                    setUser(null);
                }
            })();
        }
    }, [userLoginStatus]);

    const handleAccountMenuOpen = (event: React.MouseEvent): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleAccountMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleAccountsClick = (): void => {
        setIsAccountsOpen(!isAccountsOpen);
    };

    const handleAccountItemClick = async (newAccountId: string): Promise<void> => {
        try {
            const axiosResponse = await ApiService.post({
                url: `${getTTCBaseUrl()}/iam/v2/users/${userDetails.id}/switch-account`,
                body: `"${newAccountId}"`,
            }) as AxiosResponse;

            // if account switch was successful kick the users back out to the main page to refresh the token
            if (axiosResponse.status === 200) {
                sessionStorage.setItem('networkInsightsRedirectUrl', publicRoutes.root);
                navigate(publicRoutes.root);
            } else {
                throw new Error('Unable to switch accounts');
            }
        } catch (error) {
            console.error('Unable to switch user account', error);
            toast.error('Unable to switch user account');
            throw error;
        }
    };

    const renderUserDetails = (): React.JSX.Element => {
        if (userDetails.ttcUserObjectId) {
            return (
                <Fragment>
                    <Avatar src={user?.profile.picture} />
                    <Typography variant='body1'>
                        {user?.profile.name}
                    </Typography>
                    <Typography variant='body2'>
                        {user?.profile.email}
                    </Typography>
                    <Typography variant='body2' gutterBottom>
                        {userDetails.accountName}
                    </Typography>
                </Fragment>
            );
        }

        if (userLoginStatus === ApiStatus.Idle || userLoginStatus === ApiStatus.Loading) {
            return <OverlaySpinner noBackground />;
        }

        return <Avatar />;
    };

    return (
        <Fragment>
            <Tooltip title='Account'>
                <IconButton
                    color='inherit'
                    size='large'
                    aria-controls='menu-appbar-account'
                    aria-haspopup='true'
                    onClick={handleAccountMenuOpen}
                >
                    <Icon path={mdiAccountCircle} size={1} />
                </IconButton>
            </Tooltip>
            <StyledMenu
                classes={{
                    paper: classes.menu,
                }}
                id='menu-appbar-account'
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                disablePortal={true}
                keepMounted={true}
                open={Boolean(anchorEl)}
                onClose={handleAccountMenuClose}
            >
                <Grid
                    container
                    direction='column'
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {renderUserDetails()}
                </Grid>

                <Divider />

                {
                    userDetails.accounts.length > 1 &&
                    <List disablePadding>
                        <ListItemButton onClick={handleAccountsClick}>
                            <ListItemIcon>
                                <Icon path={mdiAccountMultipleOutline} size={1} />
                            </ListItemIcon>
                            <ListItemText primary='Accounts' />
                            <Icon path={isAccountsOpen ? mdiChevronUp : mdiChevronDown} size={1} />
                        </ListItemButton>
                        <Collapse in={isAccountsOpen} timeout='auto'>
                            <List dense disablePadding>
                                {
                                    userDetails.accounts.map((account) => {
                                        return (
                                            <ListItemButton
                                                key={account.id}
                                                className={`${classes.listItem} ${classes.nested}`}
                                                classes={{
                                                    selected: classes.listItemSelected,
                                                }}
                                                selected={account.id === userDetails.accountId}
                                                onClick={(): void => {
                                                    handleAccountItemClick(account.id);
                                                }}
                                            >
                                                {account.name}
                                            </ListItemButton>
                                        );
                                    })
                                }
                            </List>
                        </Collapse>
                    </List>
                }

                {
                    userDetails.accounts.length > 1 &&
                    <Divider />
                }

                <ListItemButton
                    onClick={(): void => {
                        sessionStorage.removeItem('networkInsightsRedirectUrl');
                        handleAccountMenuClose();
                        AuthService.logout();
                    }}
                >
                    <ListItemIcon>
                        <Icon path={mdiLogoutVariant} size={1} />
                    </ListItemIcon>
                    <Typography variant='inherit'>Log Out</Typography>
                </ListItemButton>
            </StyledMenu>
        </Fragment>
    );
};

export default UserMenu;
